import { MapPin, Clock, Camera, Crown } from 'lucide-react';
import { motion } from 'motion/react';
import { Order } from '../types/order';
import { formatDistanceToNow } from '../utils/dateUtils';
import { UrgentBadge } from './UrgentBadge';
import { LikeBadge } from './LikeBadge';
import { DislikeBadge } from './DislikeBadge';
import { ReportBadge } from './ReportBadge';
import { useState } from 'react';

interface OrderListItemProps {
  order: Order;
  onClick: () => void;
  onMarkAsViewed?: (orderId: string) => void;
  sectionColor?: string;
  orderAction?: 'like' | 'dislike' | 'dislike-removed' | 'report' | null;
  disabled?: boolean;
  isFirstInSection?: boolean;
  section?: 'express' | 'para-voce' | 'outros';
}

export function OrderListItem({
  order,
  onClick,
  onMarkAsViewed,
  sectionColor = 'var(--muted-foreground)',
  orderAction,
  disabled = false,
  isFirstInSection = false,
  section,
}: OrderListItemProps) {
  const [isPressed, setIsPressed] = useState(false);

  const {
    id,
    customerName,
    category,
    serviceType,
    createdAt,
    distance,
    neighbourhood,
    unlockedCount,
    maxUnlocks,
    isUrgent,
    isViewed,
    isHighlighted,
    attachedImages = 0,
  } = order;

  const isFirstToUnlock = unlockedCount === 0;

  const handleClick = () => {
    if (!isViewed && onMarkAsViewed) {
      onMarkAsViewed(id);
    }
    onClick();
  };

  // Get background color based on section and viewed state
  const getBackgroundColor = () => {
    if (isPressed) {
      return 'var(--muted)';
    }

    if (!isViewed) {
      return 'var(--unread-bg)'; // White for unviewed
    }

    // For viewed items, use section-specific background
    if (section === 'express') {
      return 'var(--express-viewed-bg)';
    } else if (section === 'para-voce') {
      return 'var(--para-voce-viewed-bg)';
    } else if (section === 'outros') {
      return 'var(--outros-viewed-bg)';
    }

    // Fallback to default read background
    return 'var(--read-bg)';
  };

  return (
    <motion.div
      onClick={handleClick}
      onMouseDown={() => !disabled && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => !disabled && setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      className="relative cursor-pointer"
      style={{
        backgroundColor: getBackgroundColor(),
        borderTop: '1px solid var(--neutral-200)',
        borderLeft: '1px solid var(--neutral-200)',
        borderRight: '1px solid var(--neutral-200)',
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
        opacity: disabled ? 0.6 : 1,
        filter: disabled ? 'saturate(0.7)' : 'none',
        borderTopLeftRadius: isFirstInSection ? '16px' : '0',
        borderTopRightRadius: '0',
        transition:
          'background-color 0.15s ease, transform 0.15s cubic-bezier(0.25, 0.1, 0.25, 1), border-top-left-radius 0.3s ease',
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: disabled ? 0.6 : 1,
        scale: isPressed ? 0.995 : 1,
      }}
      transition={{
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      whileTap={!disabled ? { scale: 0.995 } : {}}
    >
      {/* Compact Layout - Everything in one row when possible */}
      <div
        className="flex items-start justify-between gap-3"
        style={{ marginBottom: '6px' }}
      >
        {/* Left side: Name + Category inline */}
        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-2"
            style={{ marginBottom: '4px' }}
          >
            {/* Blue dot indicator for unseen items - 9px (2px larger), aligned with name */}
            <motion.div
              className="shrink-0"
              style={{
                width: !isViewed ? 'var(--unread-indicator-size)' : '9px',
                height: !isViewed ? 'var(--unread-indicator-size)' : '9px',
                backgroundColor: 'var(--unread-indicator)',
                borderRadius: !isViewed
                  ? 'calc(var(--unread-indicator-size) / 2)'
                  : '4.5px',
                visibility: isViewed ? 'hidden' : 'visible',
                opacity: isViewed ? 0 : 1,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: isViewed ? 0 : 1,
                opacity: isViewed ? 0 : 1,
              }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 20,
                delay: 0.05,
              }}
            />
            {/* Name - Gmail style: 16px, Bold (unread) or Regular (read) */}
            <h4
              className="truncate"
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: isViewed
                  ? 'var(--font-weight-normal)'
                  : 'var(--font-weight-bold)',
                lineHeight: '1.25',
                color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
                transition: 'font-weight 0.15s ease, color 0.15s ease',
              }}
            >
              {customerName}
            </h4>
          </div>

          {/* Category - Gmail style: 14px, Bold (unread) or Regular (read) */}
          <p
            className="truncate"
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-normal)',
              lineHeight: '1.35',
              color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
              marginLeft: '17px', // 9px dot + 8px gap = 17px
              transition: 'color 0.15s ease',
            }}
          >
            {category}
          </p>
        </div>

        {/* Right side: Time - Gmail style: 12px, Bold (unread) or Regular (read) */}
        {/*<div
          className="flex items-center gap-1 flex-shrink-0"
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: isViewed
              ? 'var(--font-weight-normal)'
              : 'var(--font-weight-bold)',
            color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
            transition: 'font-weight 0.15s ease, color 0.15s ease',
          }}
        >
          <Clock
            className="w-3.5 h-3.5"
            style={{
              width: '14px',
              height: '14px',
              color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
              transition: 'color 0.15s ease',
            }}
          />
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatDistanceToNow(createdAt)}
          </span>
        </div>*/}
      </div>

      {/* Meta Info in one compact row - 8px spacing from category */}
      <div
        className="flex items-center gap-3"
        style={{ marginLeft: '17px', marginBottom: '8px' }}
      >
        {/* Distance • Neighbourhood • Service Type + Images - Gmail style: 12px, Regular */}
        <div
          className="flex items-center gap-3"
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-normal)',
            color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
            transition: 'color 0.15s ease',
          }}
        >
          <div className="flex items-center gap-1">
            <MapPin
              className="w-3.5 h-3.5"
              style={{
                width: '14px',
                height: '14px',
                color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
                transition: 'color 0.15s ease',
              }}
            />
            <span>
              {neighbourhood} • {serviceType}
            </span>
          </div>
          {attachedImages > 0 && (
            <div className="flex items-center gap-1">
              <Camera
                className="w-3.5 h-3.5"
                style={{
                  width: '14px',
                  height: '14px',
                  color: isViewed ? 'var(--read-text)' : 'var(--unread-text)',
                  transition: 'color 0.15s ease',
                }}
              />
              <span>{attachedImages}</span>
            </div>
          )}
        </div>
      </div>

      {/* Badges row - Unlock status + Urgent/Express badges + Evaluation badges */}
      <div
        className="flex items-center gap-2 mt-2"
        style={{ marginLeft: '17px' }}
      >
        {/* Unlock status badge */}
        {isFirstToUnlock ? (
          <motion.div
            className="flex items-center gap-1.5 px-2.5 py-1"
            style={{
              backgroundColor: 'rgba(0, 153, 66, 0.14)',
              borderRadius: '6px',
              gap: '6px',
              padding: '6px 8px',
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.05 }}
          >
            <Crown
              className="w-3.5 h-3.5"
              style={{
                color: '#006937',
                fill: '#006937',
                width: 12,
                height: 12,
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: '#006937',
                whiteSpace: 'nowrap',
              }}
            >
              Seja o primeiro
            </span>
          </motion.div>
        ) : (
          <div
            className="flex items-center px-2.5 py-1"
            style={{
              borderRadius: '6px',
              gap: '6px',
              padding: '6px 8px',
              backgroundColor:
                unlockedCount > 0
                  ? 'rgba(0, 0, 0, 0.05)'
                  : 'var(--first-unlock-bg)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--muted-foreground)',
                whiteSpace: 'nowrap',
              }}
            >
              {unlockedCount}/{maxUnlocks}{' '}
              {unlockedCount === 1 ? 'liberou' : 'liberaram'}
            </span>
          </div>
        )}

        {/* Urgent badge only - Express badge shows only in details modal */}
        {isUrgent && <UrgentBadge />}

        {/* Evaluation badges */}
        {orderAction === 'like' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <LikeBadge iconOnly />
          </motion.div>
        )}
        {orderAction === 'dislike' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <DislikeBadge iconOnly />
          </motion.div>
        )}
        {orderAction === 'dislike-removed' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <DislikeBadge iconOnly />
          </motion.div>
        )}
        {orderAction === 'report' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <ReportBadge />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
